import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Switch } from 'app/components/ui/switch';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import { Separator } from 'app/components/ui/separator';
import {
  Database,
  Zap,
  ExternalLink,
  Info,
  AlertCircle,
  CheckCircle,
  Clock,
  Shield,
  Activity,
  Server,
  Globe,
  Lock,
  Network,
  Cloud,
  Download,
  Upload,
  Save,
  AlertTriangle,
} from 'lucide-react';
import { useSettingsSlice } from '../slice';

interface RedisSettingsProps {
  form: UseFormReturn<any>;
}

// Helper function to create payload with is_public: 0 for all keys
const createRedisSettingsPayload = (formData: any) => {
  const payload: Record<string, { option_value: any; is_public: number }> = {};

  // Add is_public: 0 for all Redis settings (sensitive data)
  const settingsKeys = [
    'redis_host',
    'redis_port',
    'redis_password',
    'redis_database',
    'redis_enabled',
    'redis_connection_timeout',
    'redis_read_timeout',
    'redis_write_timeout',
    'redis_retry_attempts',
    'redis_retry_delay',
  ];

  settingsKeys.forEach(key => {
    if (formData[key] !== undefined) {
      payload[key] = {
        option_value: formData[key],
        is_public: 0,
      };
    }
  });

  return payload;
};

export default function RedisSettings({ form }: RedisSettingsProps) {
  const [showPassword, setShowPassword] = React.useState(false);
  const [updateStatus, setUpdateStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const { useSaveSiteSettingsMutation } = useSettingsSlice();
  const [saveSiteSettings, { isLoading: isUpdating }] =
    useSaveSiteSettingsMutation();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Function to handle settings update with is_public: 0 for all keys
  const handleUpdateSettings = async () => {
    try {
      setUpdateStatus('idle');
      const formData = form.getValues();
      const payload = createRedisSettingsPayload(formData);

      console.log('Redis settings payload with is_public: 0:', payload);

      // Use the Redux mutation to save settings
      const result = await saveSiteSettings(payload).unwrap();
      console.log('Redis settings updated successfully:', result);

      setUpdateStatus('success');
      return result;
    } catch (error) {
      console.error('Error updating Redis settings:', error);
      setUpdateStatus('error');
      throw error;
    }
  };

  return (
    <>
      {/* Redis Cluster Enable/Disable */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="w-5 h-5" />
            Redis Cluster Configuration
          </CardTitle>
          <CardDescription>
            Configure your Redis cluster for high availability and performance
            caching.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="redis_enabled"
              checked={form.watch('redis_enabled') == 1 ? true : false}
              onCheckedChange={value => form.setValue('redis_enabled', value)}
            />
            <Label htmlFor="redis_enabled" className="text-base font-medium">
              Enable Redis Cluster
            </Label>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium mb-2">
                  Why use a Redis Cluster?
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>
                    • <strong>High Availability:</strong> Automatic failover and
                    data replication
                  </li>
                  <li>
                    • <strong>Scalability:</strong> Distribute cache across
                    multiple nodes
                  </li>
                  <li>
                    • <strong>Performance:</strong> Low-latency, in-memory data
                    access
                  </li>
                  <li>
                    • <strong>Resilience:</strong> Survive node failures without
                    downtime
                  </li>
                  <li>
                    • <strong>Cost Optimization:</strong> Efficient resource
                    usage for caching
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Redis Cluster Nodes */}
      {form.watch('redis_enabled') && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Network className="w-5 h-5" />
              Cluster Nodes
            </CardTitle>
            <CardDescription>
              Configure your Redis cluster nodes and connection settings.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="redis_host">Primary Node Host</Label>
                <Input
                  id="redis_host"
                  {...form.register('redis_host')}
                  placeholder="redis-cluster.yourdomain.com"
                />
                <div className="flex justify-between items-center">
                  <p className="text-xs text-muted-foreground">
                    Redis cluster endpoint (host)
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      copyToClipboard(
                        form.watch('redis_host') ||
                          'redis-cluster.yourdomain.com',
                      )
                    }
                    className="text-xs"
                  >
                    Copy
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="redis_port">Port</Label>
                <Input
                  id="redis_port"
                  {...form.register('redis_port')}
                  placeholder="6379"
                  type="number"
                />
                <p className="text-xs text-muted-foreground">
                  Redis port (default: 6379, TLS: 6380)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="redis_username">Username (optional)</Label>
                <Input
                  id="redis_username"
                  {...form.register('redis_username')}
                  placeholder="default"
                />
                <p className="text-xs text-muted-foreground">
                  Redis ACL username (if applicable)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="redis_password">Password</Label>
                <div className="relative">
                  <Input
                    id="redis_password"
                    {...form.register('redis_password')}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="your-redis-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 h-6 w-6 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <AlertCircle className="w-3 h-3" />
                    ) : (
                      <CheckCircle className="w-3 h-3" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Redis password or access token
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="redis_db">Database Number</Label>
                <Input
                  id="redis_db"
                  {...form.register('redis_db')}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">
                  Redis database index (default: 0)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Redis Performance Configuration */}
      {form.watch('redis_enabled') && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Performance & Connection Settings
            </CardTitle>
            <CardDescription>
              Optimize Redis cluster performance and connection reliability.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="redis_connection_timeout">
                  Connection Timeout (ms)
                </Label>
                <Input
                  id="redis_connection_timeout"
                  {...form.register('redis_connection_timeout')}
                  placeholder="10000"
                  type="number"
                />
                <p className="text-xs text-muted-foreground">
                  Timeout for connecting to Redis (default: 10000ms)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="redis_max_connections">Max Connections</Label>
                <Input
                  id="redis_max_connections"
                  {...form.register('redis_max_connections')}
                  placeholder="100"
                  type="number"
                />
                <p className="text-xs text-muted-foreground">
                  Maximum concurrent connections to Redis
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="redis_retry_attempts">Retry Attempts</Label>
                <Input
                  id="redis_retry_attempts"
                  {...form.register('redis_retry_attempts')}
                  placeholder="3"
                  type="number"
                />
                <p className="text-xs text-muted-foreground">
                  Number of retry attempts on failure (default: 3)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="redis_retry_delay">Retry Delay (ms)</Label>
                <Input
                  id="redis_retry_delay"
                  {...form.register('redis_retry_delay')}
                  placeholder="1000"
                  type="number"
                />
                <p className="text-xs text-muted-foreground">
                  Delay between retry attempts (default: 1000ms)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="redis_keep_alive">
                  Keep Alive Interval (seconds)
                </Label>
                <Input
                  id="redis_keep_alive"
                  {...form.register('redis_keep_alive')}
                  placeholder="30"
                  type="number"
                />
                <p className="text-xs text-muted-foreground">
                  Interval for keep-alive/health checks (default: 30s)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fixed Save Button - Bottom Right */}
      <div className="fixed bottom-14 right-6 z-50">
        <div className="flex flex-col items-end gap-3">
          {/* Status Feedback */}
          {updateStatus === 'success' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Redis settings saved successfully!
                </span>
              </div>
            </div>
          )}

          {updateStatus === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Failed to save Redis settings. Please try again.
                </span>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleUpdateSettings}
            className="shadow-lg hover:shadow-xl transition-all duration-200"
            variant="default"
            disabled={isUpdating}
            size="lg"
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
